import Combobox from "@/components/custom/Combobox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SelectCrmSystemSanitizedCombobox } from "@/features/crm_system/components/molecules/SelectCrmSystemSanitizedCombobox";
// import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { useGetOrganisationCrmList } from "@/features/crm/hooks/useQuery/useGetOrganisationCrmList";
import { useGetCrmProjectsByCrmId } from "@/features/crm_project/hooks/useQuery/useGetCrmProjectsByCrmId";
import { useOrganizationStore } from "@/lib/zustand/useStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";


const schema = z.object({
    name: z.string(),
    challenge: z.string(),
    crm_system: z.string().optional(),
    crm_id: z.string().optional(),
    crm_project_id: z.string().optional(),
})
export type schemaType = z.infer<typeof schema>

export type AddProjectModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    onSubmit: (data: schemaType) => void;
}

export function AddProjectModal({
    open,
    setOpen,
    onSubmit
}: AddProjectModalProps) {
    const { organization } = useOrganizationStore()
    const { data: crmsData } = useGetOrganisationCrmList(organization?.id);
    const form = useForm<schemaType>({
        resolver: zodResolver(schema)
    })

    const selectedCrm = form.watch('crm_id')
    const selectedCrmSystem = form.watch('crm_system')

    const { data: crmProjectsData } = useGetCrmProjectsByCrmId(Number(selectedCrm));
    useEffect(() => {
        form.reset()
    }, [open, form])
    // const [crmSelectKey, setCrmSelectKey] = useState(new Date());
    return (
        <Dialog open={open} onOpenChange={setOpen} defaultOpen={false}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className='px-1 m-0 font-bold text-start'>
                        Add New Project
                    </DialogTitle>
                </DialogHeader>
                <ScrollArea className="max-h-[60vh] sm:max-h-[70vh]">
                    <Form {...form}>
                        <form className="px-1 py-1 space-y-2">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Project Name</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="challenge"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Project Description</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="crm_system"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>CRM System (optional)</FormLabel>
                                        {/* <Select onValueChange={field.onChange} value={crmRegistry.getSystemById(field.value)?.id} key={crmSelectKey.toString()}> */}
                                            <FormControl>
                                                <SelectCrmSystemSanitizedCombobox
                                                    mode='single'
                                                    selectedValue={field.value || ""}
                                                    setSelectedValue={field.onChange}
                                                />
                                            </FormControl>
                                        {/* </Select> */}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="crm_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>CRM (optional)</FormLabel>
                                        {crmsData && crmsData.length ? (
                                            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={!selectedCrmSystem}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {crmsData?.filter((crm) => String(crm.crm_system) === String(selectedCrmSystem))?.map((crm) => (
                                                        <SelectItem key={crm.id} value={crm.id.toString()} className="capitalize">{crm.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <FormControl>
                                                <Input value={
                                                    selectedCrmSystem
                                                        && (
                                                            crmsData?.length === 0
                                                            || !crmsData
                                                        )
                                                        ?
                                                        "No CRM available"
                                                        : ""
                                                } disabled />
                                            </FormControl>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {(crmProjectsData) && (
                                <>
                                    <FormField
                                        control={form.control}
                                        name="crm_project_id"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>CRM Project (optional)</FormLabel>
                                                <FormControl>
                                                    <Combobox
                                                        mode="single"
                                                        items={[{ label: "Clear selection", value: "" }, ...crmProjectsData?.map((crm, i) => ({ label: `${i + 1}. ${crm.name}`, value: `${crm.id}` }))]}
                                                        setSelectedValue={field.onChange}
                                                        selectedValue={field.value ? String(field.value) : null}
                                                        triggerLabel="Select CRM Project"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </>
                            )}
                        </form>
                    </Form>
                </ScrollArea>
                <div className="flex justify-end w-full px-1">
                    <div className="w-full mb-1 sm:mb-0 sm:me-2 sm:w-auto">
                        <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Cancel</Button>
                    </div>
                    <div className="w-full sm:w-auto">
                        <Button type="submit" onClick={() => form.handleSubmit(onSubmit)()}>Submit</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}